import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Bookmark, ListChecks, TreeStructure, User, MapPin, Key } from "@phosphor-icons/react";
import DateInput from "../DateInput";
import { FileDropZone } from "../FileDropZone";
import { AddQuoteChoiceDialog } from "../quotes/AddQuoteMenu";
import { NoticeEditor, EMPTY_NOTICE } from "../NoticeEditor";
import ComplianceDocsPicker from "../ComplianceDocsPicker";
import { AiBriefButton, AiSmartAssignButton } from "../AiFeatures";
import ScopeOfWorksField, { CaveatsField } from "./ScopeOfWorksField";

const CATEGORIES = ["general", "plumbing", "electrical", "carpentry", "painting", "roofing", "landscaping", "cleaning", "hvac", "fire_safety", "security", "mould_eradication", "other"];
const EMPTY = {
  title: "", category: "general", instructions: "", scope_of_works: "", caveats: "", start_date: "", end_date: "", start_time: "", end_time: "",
  assigned_vendor_id: "", assigned_vendor_name: "", assigned_operative_id: "", assigned_operative_name: "",
  checklist_template_id: "", attachments: [], job_form_ids: [], compliance_doc_ids: [], notice: { ...EMPTY_NOTICE },
};
// Template fields a sub-job may take — client / property / contacts always come from the main job.
const TEMPLATE_KEYS = ["title", "category", "instructions", "scope_of_works", "caveats", "start_time", "end_time", "job_form_ids", "checklist_template_id", "notice"];

/* What the sub-job inherits from its main job — shown read-only so the admin knows it's already covered. */
function InheritedSummary({ parent }) {
  const address = [parent?.property_name, parent?.street_address, parent?.town_city, parent?.postcode].filter(Boolean).join(", ");
  const client = parent?.client_company || parent?.client_name;
  return (
    <div className="rounded-md border border-violet-200 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/20 px-3 py-2 text-[11px] text-violet-900 dark:text-violet-200 flex flex-wrap gap-x-4 gap-y-1" data-testid="sub-job-inherited">
      <span className="font-semibold inline-flex items-center gap-1"><TreeStructure size={12} /> Inherited from {parent?.reference}:</span>
      {client && <span className="inline-flex items-center gap-1"><User size={11} /> {client}</span>}
      {address && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {address}</span>}
      {parent?.site_contact_name && <span>Site contact: {parent.site_contact_name}{parent.site_contact_phone ? ` · ${parent.site_contact_phone}` : ""}</span>}
      {parent?.access_notes && <span className="inline-flex items-center gap-1"><Key size={11} /> Access notes</span>}
    </div>
  );
}

/* Add a sub-job to a main job — same form as New Job minus the client / property block (inherited server-side). */
export default function SubJobDialog({ open, onOpenChange, parent, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [vendors, setVendors] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const [thenQuote, setThenQuote] = useState(false);
  const [quoteFor, setQuoteFor] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    API.get("/suppliers?limit=500").then(({ data }) => setVendors(Array.isArray(data) ? data : data.vendors || [])).catch(() => {});
    API.get("/in-house/team").then(({ data }) => setOperatives((data.team || []).filter((u) => u.active !== false))).catch(() => {});
    API.get("/job-forms").then(({ data }) => setForms(data.forms || [])).catch(() => {});
    API.get("/work-templates?template_type=job").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
    API.get("/checklist-templates").then(({ data }) => setChecklists((data.templates || []).filter((t) => t.active !== false))).catch(() => {});
    API.get("/compliance-hub/documents").then(({ data }) => setComplianceDocs(data.documents || [])).catch(() => {});
  }, [open]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const applyTemplate = (id) => {
    const tpl = templates.find((t) => t.id === id);
    if (!tpl?.fields) return;
    set(Object.fromEntries(Object.entries(tpl.fields).filter(([k]) => TEMPLATE_KEYS.includes(k))));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Give the sub-job a title"); return; }
    setSaving(true);
    try {
      const { compliance_doc_ids, ...payload } = form;
      const { data } = await API.post(`/jobs/${parent.id}/sub-jobs`, payload);
      if (compliance_doc_ids.length) {
        try { await API.post(`/jobs/${data.id}/compliance-hub/link`, { doc_ids: compliance_doc_ids }); }
        catch (err) { toast.warning(`Sub-job created, but compliance doc linking failed: ${err?.response?.data?.detail || err.message}`); }
      }
      toast.success(`Sub-job ${data.sub_job_index} added — ${data.reference}${compliance_doc_ids.length ? ` · ${compliance_doc_ids.length} compliance doc${compliance_doc_ids.length === 1 ? "" : "s"} linked` : ""}`);
      onCreated?.(data);
      if (thenQuote) { onOpenChange(false); setQuoteFor(data); return; }
      if (addAnother) setForm({ ...EMPTY, category: form.category, start_date: form.start_date }); else onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add sub-job"); }
    finally { setSaving(false); }
  };

  const propertyAddress = [parent?.street_address, parent?.town_city, parent?.postcode].filter(Boolean).join(", ");
  const scopeContext = { entity: "job", category: form.category, title: form.title, description: form.instructions, client_name: parent?.client_company || parent?.client_name, property_address: propertyAddress };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="sub-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Add sub-job to {parent?.reference}</DialogTitle>
          <DialogDescription className="text-xs">One issue at the property = one sub-job. Everything below works exactly like a normal job — it can be assigned, scheduled, quoted, given a works order and completed on its own; the main job tracks progress and raises the single invoice.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <InheritedSummary parent={parent} />
          {templates.length > 0 && (
            <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-md border border-dashed border-border" data-testid="sub-job-template-selector">
              <Bookmark size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground shrink-0">Apply Template:</span>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="sub-job-apply-template"><SelectValue placeholder="Select a template to pre-fill..." /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between">
              <Label>What needs doing? *</Label>
              <AiBriefButton workType="job" category={form.category} propertyAddress={parent?.street_address}
                onApply={(brief) => set({ title: brief.title || form.title, instructions: brief.description || form.instructions, category: brief.suggested_category || form.category })} />
            </div>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} required autoFocus placeholder="e.g. Leaking tap in kitchen" className="mt-1" data-testid="sub-job-title" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Trade / category</Label>
              <Select value={form.category} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger className="mt-1" data-testid="sub-job-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1"><ListChecks size={12} /> Checklist</Label>
              <Select value={form.checklist_template_id || "auto"} onValueChange={(v) => set({ checklist_template_id: v === "auto" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="sub-job-checklist"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (match category)</SelectItem>
                  {checklists.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Assign Supplier</Label>
                <AiSmartAssignButton category={form.category} title={form.title} description={form.instructions} area={parent?.town_city || parent?.county}
                  onSelect={(rec) => set({ assigned_vendor_id: rec.vendor_id, assigned_vendor_name: rec.vendor_name })} />
              </div>
              <Select value={form.assigned_vendor_id || "none"} onValueChange={(v) => {
                if (v === "none") { set({ assigned_vendor_id: "", assigned_vendor_name: "" }); return; }
                const vnd = vendors.find((x) => x.id === v);
                set({ assigned_vendor_id: v, assigned_vendor_name: vnd?.name || vnd?.company_name || "" });
              }}>
                <SelectTrigger data-testid="sub-job-vendor"><SelectValue placeholder="No supplier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No supplier assigned</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name || v.company_name} — {v.trade || "General"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign In-House Operative</Label>
              <Select value={form.assigned_operative_id || "none"} onValueChange={(v) => {
                if (v === "none") { set({ assigned_operative_id: "", assigned_operative_name: "" }); return; }
                const op = operatives.find((x) => x._id === v);
                set({ assigned_operative_id: v, assigned_operative_name: op?.name || op?.email || "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="sub-job-operative"><SelectValue placeholder="No operative" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No operative assigned</SelectItem>
                  {operatives.map((op) => <SelectItem key={op._id} value={op._id}>{op.name || op.email}{op.trade ? ` — ${op.trade}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Optionally assign an in-house team member alongside or instead of a supplier.</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">SCHEDULE</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Start Date</Label><DateInput value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className="mt-1" data-testid="sub-job-start-date" /></div>
              <div><Label>End Date <span className="text-[10px] text-muted-foreground">(optional)</span></Label><DateInput value={form.end_date} min={form.start_date || undefined} onChange={(e) => set({ end_date: e.target.value })} className="mt-1" data-testid="sub-job-end-date" /></div>
              <div><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={(e) => set({ start_time: e.target.value })} className="mt-1" data-testid="sub-job-start-time" /></div>
              <div><Label>End Time</Label><Input type="time" value={form.end_time} onChange={(e) => set({ end_time: e.target.value })} className="mt-1" data-testid="sub-job-end-time" /></div>
            </div>
          </div>
          <div>
            <Label>Instructions</Label>
            <Textarea rows={3} value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} className="mt-1" placeholder="Any special instructions for the supplier or engineer on this item…" data-testid="sub-job-instructions" />
          </div>
          <ScopeOfWorksField value={form.scope_of_works} onChange={(v) => set({ scope_of_works: v })} testid="sub-job-scope-of-works" context={scopeContext}
            sets={{ caveats: form.caveats, notice: form.notice, onApply: (v) => set(v) }} />
          <CaveatsField value={form.caveats} onChange={(v) => set({ caveats: v })} scope={form.scope_of_works} testid="sub-job-caveats" context={scopeContext} />
          <NoticeEditor value={form.notice} onChange={(n) => set({ notice: n })} clientToggle aiContext={{ entity: "job", title: form.title, description: form.instructions }} testid="sub-job-notice" compact />
          <div>
            <Label>Supporting Images / Files</Label>
            <FileDropZone files={form.attachments} onFilesChange={(files) => set({ attachments: files })} folder="jobs" accept="all" maxFiles={10} className="mt-1" />
          </div>
          {forms.length > 0 && (
            <div>
              <Label>Job Forms</Label>
              <p className="text-[10px] text-muted-foreground mb-1">Select forms the supplier or operative needs to complete for this sub-job</p>
              <div className="space-y-1.5 mt-1 max-h-32 overflow-y-auto border border-border rounded-md p-2">
                {forms.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 px-2 py-1 rounded">
                    <input type="checkbox" className="rounded" checked={form.job_form_ids.includes(f.id)} onChange={(e) => set({ job_form_ids: e.target.checked ? [...form.job_form_ids, f.id] : form.job_form_ids.filter((x) => x !== f.id) })} data-testid={`sub-job-form-${f.id}`} />
                    <span>{f.title}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{f.fields?.length || 0} fields</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {complianceDocs.length > 0 && (
            <div>
              <Label>Compliance Documents</Label>
              <p className="text-[10px] text-muted-foreground mb-1">Operatives must acknowledge each linked document before they can start this sub-job.</p>
              <ComplianceDocsPicker docs={complianceDocs} selectedIds={form.compliance_doc_ids} onChange={(ids) => set({ compliance_doc_ids: ids })} />
            </div>
          )}
          <DialogFooter className="items-center gap-3 sm:justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" className="rounded" checked={addAnother} onChange={(e) => setAddAnother(e.target.checked)} disabled={thenQuote} data-testid="sub-job-add-another" /> Keep open to add another
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none font-medium text-emerald-800 dark:text-emerald-200" title="After saving, choose how to add the quote for this sub-job">
                <input type="checkbox" className="rounded" checked={thenQuote} onChange={(e) => { setThenQuote(e.target.checked); if (e.target.checked) setAddAnother(false); }} data-testid="sub-job-then-quote" /> Then add a quote
              </label>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="sub-job-submit">{saving ? "Adding…" : thenQuote ? "Add sub-job & quote" : "Add sub-job"}</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
      <AddQuoteChoiceDialog job={quoteFor} open={!!quoteFor} onOpenChange={(o) => { if (!o) setQuoteFor(null); }} />
    </Dialog>
  );
}
